import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useArchivedTeachers, useRestoreTeacher } from "hooks/api/teachers";
import { useTeacherColumns } from "hooks/table/useTeacherColumns";
import { useRole } from "hooks/common";
import { Divider } from "components/Common/Divider";
import { Empty } from "components/Common/Empty";
import { Table } from "components/Table";
import { StudySearch } from "../../../../components/StudySearch";
import { TeachersContent } from "content";
import { Route } from "constants/routes";
import { logError, logSuccess } from "utils/notifications";
import { TTeacher } from "types/api/teacher";
import { TSelectOption } from "types/app/select";
import { EStudySearchTarget } from "types/app/study";
import "../../Teachers.styles.scss";

const PAGINATION_ENABLED = false;

export const TeachersArchive: React.FC = () => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);

  const { isUserAdmin } = useRole();

  const { data, isLoading, refetch } = useArchivedTeachers({
    searchText: searchValue,
    pageNumber,
    pageSize: PAGINATION_ENABLED ? undefined : 99999,
  });

  const { mutate: restoreTeacher } = useRestoreTeacher();

  const teachers: TTeacher[] = useMemo(
    () => data?.content || [],
    [data?.content]
  );
  const isEmpty: boolean = teachers.length === 0;

  const searchCallback = useCallback(
    async (): Promise<TSelectOption<number>[]> => [],
    []
  );

  const handleRestoreTeacher = useCallback(
    (teacher: TTeacher) => {
      restoreTeacher(teacher.id, {
        onSuccess: () => {
          logSuccess(
            `Вчителя ${teacher.person.firstName} ${teacher.person.lastName} відновлено`
          );
          refetch();
        },
        onError: (error) => {
          logError({ error });
        },
      });
    },
    [restoreTeacher, refetch]
  );

  const columns = useTeacherColumns({
    onEditClick: handleRestoreTeacher,
    hideDelete: true,
    editButtonLabel: "Відновити",
  });

  return (
    <>
      <StudySearch
        searchTarget={EStudySearchTarget.Teachers}
        searchPlaceholder={TeachersContent.Search.PLACEHOLDER}
        searchCallback={searchCallback}
        setSearchValue={setSearchValue}
        hideAddButton
      />
      {isUserAdmin && (
        <div className="nb-sub-teachers-filter-links">
          <Link
            to={Route.App.Study.TEACHERS.path}
            className="nb-sub-teachers-filter-link"
          >
            Всі
          </Link>
          <Link
            to={Route.App.Study.TEACHERS_ARCHIVE.path}
            className="nb-sub-teachers-filter-link active"
          >
            Архів
          </Link>
        </div>
      )}
      <Divider margin="bottom" />
      {isEmpty && !isLoading ? (
        <Empty text="Архів вчителів порожній" />
      ) : (
        <Table<TTeacher>
          data={teachers}
          columns={columns}
          loading={isLoading}
          totalItems={data?.totalElements}
          onPageChange={setPageNumber}
          hideFooter={!PAGINATION_ENABLED}
        />
      )}
    </>
  );
};
